<template>
  <div>
    <!-- Header -->
    <div class="p-5 flex items-center justify-between w-full">
      <div class="flex items-center gap-2">
        <BaseText type="label" size="sm" class="text-text-muted">
          <span> New Share Link</span>
        </BaseText>
      </div>
      <button class="black-btn-primary py-1.5 px-3 w-36 rounded-md flex items-center justify-center text-white"
      :disabled="!isFormValid" @click="$emit('createShare')">
        <BaseLoadingSpinnerCircle v-if="loading.createShare" />
        <BaseText v-else type="label" size="sm" class="whitespace-nowrap">
          Create Share Link
        </BaseText>
      </button>
    </div>
    <!-- SHARE TYPE SELECTION -->
    <div class="flex justify-center items-center gap-3 px-5">
      <div v-for="(shareType) in shareLinkTypes" :key="shareType.id"
      class="rounded-lg border p-3 group  transition-colors cursor-pointer space-y-2 w-full h-28"
      :class="getShareSelectClass(shareType.id)"
      @click="updateShareForm('type', shareType.id)">
        <div class="flex items-center gap-2">
          <component :is="shareType.icon" class="text-icon-normal group-hover:text-icon-muted" />
          <BaseText type="label" size="sm" class="text-text-normal group-hover:text-text-muted">
            {{ shareType.name }}
          </BaseText>
        </div>
        <BaseText size="sm" class="text-text-normal group-hover:text-text-muted">
          {{ shareType.description }}
        </BaseText>
      </div>
    </div>
    <!-- Updating Share Report Disclaimer -->
    <div v-if="shareForm.type === 'updating'" class="mt-3 px-5 pb-1">
      <div class="p-2 flex items-start gap-2 rounded-xl bg-primary-blue-10">
        <InfoRectIcon class="text-primary-blue-100 w-5 h-5 flex-shrink-0" />
        <div class="space-y-1">
          <BaseText type="label" size="xs" class="text-primary-blue-300">
            Updating reports act like a leaderboard.
          </BaseText>
          <BaseText size="xs" style="color: rgba(7, 36, 126, 0.68)">
            Eg. If you share a report sorted by spend, and you select 5 ads to display - Those ads will be swapped if different ads outspend them in the future.
          </BaseText>
        </div>
      </div>
    </div>
    <!-- SHARE FORM INPUTS -->
    <div class="p-5 space-y-3 w-full">
      <div>
        <!-- (Updating) Relative Date Range Selector -->
        <BaseSingleDropdown
          v-if="shareForm.type === 'updating'"
          label="Live Date Range"
          placeholder="Select a rolling period"
          class="z-30 relative"
          use-parent-width
          show-label-space
          option-key="id"
          :selected-obj="shareForm.rollingDateRange"
          :options="rollingDateRangeOptions"
          @change="($event) => updateShareForm('rollingDateRange', $event)"
        />
        <!-- (Static) Date Range Display -->
        <div v-else class="flex flex-col gap-1 items-start w-full relative">
          <BaseText type="label" size="sm" class="text-text-muted">
            Static Date Range
          </BaseText>
          <div class="form-input w-full flex items-center gap-2 py-1.5 px-2">
            <CalendarIcon class="text-icon-disabled" />
            <BaseText size="sm" class="text-text-disabled">
              {{ formatDateRange }}
            </BaseText>
          </div>
        </div>
      </div>
      <!-- Report Name Display -->
      <div class="flex flex-col gap-1 w-full">
        <BaseText type="label" size="sm" class="text-text-muted">
          Report Name
        </BaseText>
        <input
          v-model="shareForm.name"
          disabled
          placeholder="Name"
          class="form-input fade-in min-w-0 w-full px-2 py-1.5"
        >
      </div>
      <!-- Report Description Display -->
      <div class="space-y-1">
        <BaseText type="label" size="sm" class="text-text-muted">
          Description
        </BaseText>
        <input
          :value="shareForm?.description || 'N/A'"
          disabled
          placeholder="Type a description of this report"
          class="form-input fade-in min-w-0 w-full px-2 py-1.5"
        >
      </div>
      <!-- Divider -->
      <div><div class="w-full border-t border-border-normal my-5" /></div>
      <!-- SECURITY SECTION -->
      <div>
        <BaseText type="heading" size="overline" class="uppercase text-icon-normal mb-2.5">
          Report Security
        </BaseText>
        <div class="flex flex-col gap-2.5">
          <!-- Enable Password Toggle-->
          <div class="flex items-center justify-between">
            <BaseText type="label" size="sm" class="text-text-muted">
              Password
            </BaseText>
            <BaseToggle
              :value="enableForm.password"
              @toggle="updateSecurityConfig('password')"
            />
          </div>
          <!-- Password Input (if enabled) -->
          <div v-if="enableForm.password" class="relative">
            <input
              v-model="shareForm.password"
              placeholder="Password"
              class="form-input fade-in min-w-0 w-full px-2 py-1.5"
            >
          </div>
          <!-- Track IPs Toggle -->
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-1">
              <BaseText type="label" size="sm" class="text-text-muted">
                Track Opens & IPs
              </BaseText>
            </div>
            <BaseToggle
              :value="enableForm.trackIps"
              @toggle="updateSecurityConfig('trackIps')"
            />
          </div>
          <!-- Comments (coming soon) -->
          <div class="flex items-center justify-between opacity-60 cursor-not-allowed">
            <div class="flex items-center gap-1">
              <BaseText type="label" size="sm" class="text-text-muted">
                Show Comments
              </BaseText>
              <BaseText size="xs" class="text-text-subdued">
                (Coming soon)
              </BaseText>
            </div>
            <BaseToggle
              :value="enableForm.showComments"
              @toggle="updateSecurityConfig('showComments')"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway2'
// Components
import DateRangeSelectorDropdown from '../../../search/TopBarComponents/DateRangeSelectorDropdown.vue'
// Icons
import LinkIcon from '../../../globals/Icons/LinkIcon.vue'
import UpdatingReportIcon from '../../../globals/Icons/LensIcons/report/UpdatingReportIcon.vue'
import StaticReportIcon from '../../../globals/Icons/LensIcons/report/StaticReportIcon.vue'
import CalendarIcon from '../../../globals/Icons/CalendarIcon.vue'
import InfoRectIcon from '../../../globals/Icons/InfoRectIcon.vue'
export default {
  name: 'ShareReportForm',
  components: {
    DateRangeSelectorDropdown,
    // Icons
    LinkIcon,
    UpdatingReportIcon,
    StaticReportIcon,
    CalendarIcon,
    InfoRectIcon
  },
  mixins: [clickaway],
  props: {
    shareForm: {
      type: Object,
      default: () => {}
    },
    loading: {
      type: Object,
      default: () => {}
    },
    enableForm: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    const shareLinkTypes = [
      {
        id: 'updating',
        name: 'Updating',
        icon: UpdatingReportIcon,
        description: 'Create a share link that updates with a rolling date range.'
      },
      {
        id: 'static',
        name: 'Static',
        icon: StaticReportIcon,
        description: 'Create a share link with a static date range and no updates.'
      }
    ]

    const rollingDateRangeOptions = [
      { id: 'last_7_days', name: 'Last 7 days' },
      { id: 'last_14_days', name: 'Last 14 days' },
      { id: 'last_30_days', name: 'Last 30 days' },
      { id: 'last_90_days', name: 'Last 90 days' },
      { id: 'last_365_days', name: 'Last 365 days' }
    ]

    const securityToggles = [
      { id: 'password', name: 'Password' },
      { id: 'trackIps', name: 'Track Opens & IPs' },
      { id: 'showComments', name: 'Show Comments' }
    ]
    return {
      shareLinkTypes,
      securityToggles,
      rollingDateRangeOptions,

      showDateRangeSelector: false,
      dateFilterLabel: ''
    }
  },
  computed: {
    isFormValid () {
      const validPassword = this.enableForm.password ? this.shareForm.password.length > 0 : true
      return this.shareForm.type && this.shareForm.name && !this.loading.createShare && validPassword
    },
    formatDateRange () {
      if (!this.shareForm.dateRange?.start || !this.shareForm.dateRange?.end) {
        return 'All Time'
      }
      const options = { year: 'numeric', month: 'short', day: '2-digit' }
      const startDate = new Date(this.shareForm.dateRange.start).toLocaleDateString('en-US', options)
      const endDate = new Date(this.shareForm.dateRange.end).toLocaleDateString('en-US', options)
      return `${startDate} to ${endDate}`
    }
  },
  mounted () {
    console.log('mounted!')
  },
  methods: {
    getShareSelectClass (shareId) {
      const borderStyle = shareId === this.shareForm.type ? 'border-neutral-600' : 'border-border-normal'
      return `${borderStyle} cursor-pointer`
    },
    handleClipboardCopy () {
      this.$showAlert({
        type: 'success',
        message: 'Copied to clipboard'
      })
    },
    updateShareForm (type, value) {
      if (type === 'dateRange') this.showDateRangeSelector = false
      this.$emit('formChange', { type, value })
    },
    updateSecurityConfig (key) {
      if (key === 'showComments') return
      this.$emit('configChange', { type: key, value: !this.enableForm[key] })
    }
  }
}
</script>

<style scoped>
.form-input {
  color: #06070F;
  border-radius: 6px;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 150ms ease-in-out;
  height: 32px;
}
.form-input:focus {
  outline: none;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.1), 0px 0px 0px 1px rgba(0, 56, 108, 0.1);
}
.form-input::placeholder {
  color: #5E6678;
  transition: color 150ms ease-in-out;
  opacity: 0.9;
}
.form-input:hover::placeholder {
  color: #303546;
}
.form-input:focus::placeholder {
  color: #C1C7D0;
}
.form-input:disabled{
  color: #A4ACB9;
  background-color: white;
  font-size: 14px;
}
.date-selector-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 500;
}

</style>
